import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import CountrySelect from '@/shared/components/input/CountrySelect';
const addressMessages = defineMessages({
    Street: { id: 'Street', defaultMessage: 'Street' },
    Number: { id: 'No.', defaultMessage: 'No.' },
    Box: { id: 'Box', defaultMessage: 'Box' },
    Street2: { id: 'Street2', defaultMessage: 'Street 2' },
    PostalCode: { id: 'PostalCode', defaultMessage: 'Postal code' },
    City: { id: 'City', defaultMessage: 'City' },
    Country: { id: 'Country', defaultMessage: 'Country' },
    Save: { id: 'Save', defaultMessage: 'Save' },
    Cancel: { id: 'Cancel', defaultMessage: 'Cancel' },
});
const emptyAddress = {
    id: null,
    street1: '',
    street2: '',
    number: '',
    box: '',
    zipCode: '',
    city: '',
    region: null,
    country: null,
};
const formatAddressLines = (addr, hideCountry, translateCountry) => {
    if (!addr)
        return [];
    const lines = [];
    let streetLine = addr.street1 || '';
    if (addr.number)
        streetLine += ` ${addr.number}`;
    if (addr.box)
        streetLine += `/${addr.box}`;
    if (streetLine.trim())
        lines.push(streetLine.trim());
    if (addr.street2)
        lines.push(addr.street2);
    const cityLine = [addr.zipCode, addr.city].filter(Boolean).join(' ');
    if (cityLine)
        lines.push(cityLine);
    if (!hideCountry && addr.country) {
        lines.push(translateCountry ? translateCountry(addr.country) : addr.country);
    }
    return lines;
};
const EditableAddress = ({ value, onSubmit, readOnly = false, hideCountry = false, }) => {
    const intl = useIntl();
    const translateCountry = (code) => intl.formatMessage(listItemMsg('country', code, code));
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(value || emptyAddress);
    const handleEdit = () => {
        if (readOnly)
            return;
        setForm(value || emptyAddress);
        setEditing(true);
    };
    const handleCancel = () => {
        setEditing(false);
    };
    const handleSave = async () => {
        setSaving(true);
        try {
            await onSubmit(form);
            setEditing(false);
        }
        finally {
            setSaving(false);
        }
    };
    const updateField = (field, val) => {
        setForm(prev => (Object.assign(Object.assign({}, prev), { [field]: val })));
    };
    if (readOnly) {
        const lines = formatAddressLines(value, hideCountry, translateCountry);
        return (_jsx("div", { children: lines.length > 0
                ? lines.map((line, i) => (_jsxs("span", { children: [i > 0 && _jsx("br", {}), line] }, i)))
                : null }));
    }
    if (saving) {
        return (_jsx("div", Object.assign({ className: "react-editable react-editable-saving" }, { children: _jsx("i", { className: "fa fa-spinner fa-spin" }) })));
    }
    if (!editing) {
        const lines = formatAddressLines(value, hideCountry, translateCountry);
        const hasContent = lines.length > 0;
        return (_jsx("a", Object.assign({ tabIndex: 0, role: "tabpanel", className: "react-editable react-editable-display", style: { display: 'inline-block' }, onClick: handleEdit, onFocus: handleEdit }, { children: hasContent ? (lines.map((line, i) => _jsx("div", { children: line }, i))) : (_jsx("i", { className: "glyphicon glyphicon-pencil" })) })));
    }
    return (_jsx("div", Object.assign({ className: "react-editable react-editable-editing react-editable-block" }, { children: _jsxs("div", { children: [_jsxs("div", Object.assign({ style: { display: 'flex', gap: 4, marginBottom: 4 } }, { children: [_jsx("input", { className: "form-control", style: { flex: 3 }, placeholder: intl.formatMessage(addressMessages.Street), value: form.street1, onChange: e => updateField('street1', e.target.value), autoFocus: true }), _jsx("input", { className: "form-control", style: { flex: 1 }, placeholder: intl.formatMessage(addressMessages.Number), value: form.number, onChange: e => updateField('number', e.target.value) }), _jsx("input", { className: "form-control", style: { flex: 1 }, placeholder: intl.formatMessage(addressMessages.Box), value: form.box, onChange: e => updateField('box', e.target.value) })] })), _jsx("input", { className: "form-control", style: { marginBottom: 4 }, placeholder: intl.formatMessage(addressMessages.Street2), value: form.street2, onChange: e => updateField('street2', e.target.value) }), _jsxs("div", Object.assign({ style: { display: 'flex', gap: 4, marginBottom: 4 } }, { children: [_jsx("input", { className: "form-control", style: { flex: 2 }, placeholder: intl.formatMessage(addressMessages.PostalCode), value: form.zipCode, onChange: e => updateField('zipCode', e.target.value) }), _jsx("input", { className: "form-control", style: { flex: 3 }, placeholder: intl.formatMessage(addressMessages.City), value: form.city, onChange: e => updateField('city', e.target.value) })] })), _jsx("div", Object.assign({ style: { marginBottom: 4 } }, { children: _jsx(CountrySelect, { value: form.country, onChange: (code) => updateField('country', code), placeholder: intl.formatMessage(addressMessages.Country) }) })), _jsxs("div", { children: [_jsxs("button", Object.assign({ type: "button", className: "btn btn-sm btn-inverse", onClick: handleSave }, { children: [saving ? (_jsx("i", { className: "fa fa-spinner fa-spin margin5R" })) : (_jsx("i", { className: "glyphicon glyphicon-floppy-disk margin5R" })), intl.formatMessage(addressMessages.Save)] })), _jsx("button", Object.assign({ type: "button", className: "btn btn-sm btn-default margin5L", onClick: handleCancel }, { children: intl.formatMessage(addressMessages.Cancel) }))] })] }) })));
};
export default EditableAddress;
